import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiClient, getActiveAiWorkspaceId, setActiveAiWorkspaceId } from '../lib/api-client';

export interface AiWorkspace {
  id: string;
  tenantCode: string;
  displayName: string;
  description?: string;
  color?: string;
  icon?: string;
  streamCount?: number;
  roleCount?: number;
  lastRunAt?: string | null;
}

type WorkspaceContextValue = {
  workspaceId: string;
  setWorkspaceId: (workspaceId: string) => void;
  workspace: AiWorkspace | null;
  workspaces: AiWorkspace[];
  refreshWorkspaces: () => Promise<void>;
  loading: boolean;
};

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ workspaceId: initialWorkspaceId, children }: { workspaceId?: string; children: React.ReactNode }) {
  const [workspaceId, setWorkspaceIdState] = useState(initialWorkspaceId || getActiveAiWorkspaceId());
  const [workspaces, setWorkspaces] = useState<AiWorkspace[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshWorkspaces = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.getAiWorkspaces();
      const next = Array.isArray(res.workspaces) ? res.workspaces : [];
      setWorkspaces(next);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshWorkspaces();
  }, [refreshWorkspaces]);

  useEffect(() => {
    if (!workspaceId) return;
    setActiveAiWorkspaceId(workspaceId);
  }, [workspaceId]);

  const setWorkspaceId = useCallback((nextId: string) => {
    const id = String(nextId || '').trim() || 'maxiron';
    setWorkspaceIdState(id);
    setActiveAiWorkspaceId(id);
  }, []);

  const workspace = useMemo(
    () => workspaces.find((w) => w.id === workspaceId) || null,
    [workspaces, workspaceId],
  );

  const value = useMemo<WorkspaceContextValue>(() => ({
    workspaceId,
    setWorkspaceId,
    workspace,
    workspaces,
    refreshWorkspaces,
    loading,
  }), [workspaceId, setWorkspaceId, workspace, workspaces, refreshWorkspaces, loading]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used inside WorkspaceProvider');
  }
  return ctx;
}
